import React, { useState, useEffect } from 'react';
import Container from '../Container';
import Button from '../../common/Button';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    const handleMouseMove = (e) => {
      const header = document.querySelector('header');
      if (header) {
        const rect = header.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contato');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pt-4 md:pt-6 px-4 md:px-0">
      <Container>
        {/* Container do header */}
        <div 
          className={`
            relative overflow-hidden
            bg-gray-800/40 backdrop-blur-md rounded-full 
            px-4 sm:px-6 md:px-8 
            py-3 md:py-4
            border border-gray-700/50 
            flex items-center justify-between
            transition-all duration-700 ease-out
            ${scrolled ? 'bg-gray-800/70 border-gray-700/70 shadow-2xl shadow-emerald-500/10' : ''}
          `}
          style={{
            transform: scrolled ? 'scale(0.98)' : 'scale(1)',
          }}
        >
          {/* Gradient animado de fundo - apenas desktop */}
          <div 
            className="hidden md:block absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)`,
            }}
          />

          {/* Borda animada - apenas desktop */}
          <div 
            className="hidden md:block absolute inset-0 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-500"
            style={{
              background: `linear-gradient(${mousePosition.x * 360}deg, rgba(16, 185, 129, 0.3), transparent)`,
              padding: '1px',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }}
          />

          {/* Logo com animações */}
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center relative z-10 group cursor-pointer"
            aria-label="Voltar ao início"
          >
            <div className="relative">
              {/* Glow atrás da logo - apenas desktop */}
              <div className="hidden md:block absolute inset-0 bg-emerald-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-150" />
              
              <img 
                src="/images/logo.png" 
                alt="Mara Labs" 
                className="h-8 md:h-10 lg:h-12 w-auto relative z-10 transition-all duration-500 md:group-hover:scale-110 md:group-hover:drop-shadow-[0_0_15px_rgba(16,185,129,0.8)] md:group-hover:brightness-110"
              />
            </div>
          </button>

          {/* CTA Button - funcional */}
          <div className="relative z-10 group">
            {/* Partículas - apenas desktop */}
            <div className="hidden lg:block absolute -inset-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-emerald-500 rounded-full animate-ping"
                  style={{
                    top: `${50 + 40 * Math.sin((i * Math.PI) / 4)}%`,
                    left: `${50 + 40 * Math.cos((i * Math.PI) / 4)}%`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '2s',
                  }}
                />
              ))}
            </div>

            {/* Anel pulsante - apenas desktop */}
            <div className="hidden md:block absolute -inset-2 bg-gradient-to-r from-emerald-500/30 to-cyan-500/30 rounded-full blur-lg opacity-0 group-hover:opacity-100 animate-pulse-slow transition-opacity duration-500" />
            
            {/* Shimmer effect - apenas desktop */}
            <div className="hidden md:block absolute inset-0 rounded-full overflow-hidden">
              <div 
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                style={{
                  transform: 'skewX(-20deg)',
                }}
              />
            </div>

            <Button 
              variant="primary"
              onClick={scrollToContact}
              className="relative shadow-lg shadow-emerald-500/50 md:group-hover:shadow-emerald-500/80 transition-all duration-300 md:group-hover:scale-105 active:scale-95 text-xs sm:text-sm px-4 sm:px-6 py-2 md:py-3"
            >
              <span className="relative z-10 hidden sm:inline">Entre em contato</span>
              <span className="relative z-10 inline sm:hidden">Contato</span>
              
              {/* Ícone animado */}
              <svg 
                className="w-4 h-4 ml-2 relative z-10 transition-all duration-300 md:group-hover:translate-x-1 md:group-hover:scale-110" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          </div>

          {/* Linha de energia */}
          <div 
            className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500 transition-all duration-700 ${
              scrolled ? 'w-full opacity-100' : 'w-0 opacity-0'
            }`}
            style={{
              boxShadow: scrolled ? '0 0 10px rgba(16, 185, 129, 0.8)' : 'none',
            }}
          />
        </div>
      </Container>
    </header>
  );
};

export default Header;